import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Box from '@risk-smart/themed-cloudscape-components/box';
import Input from '@risk-smart/themed-cloudscape-components/input';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { CatalogNode } from '@risksmart-app/domain/src/types/catalog';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyCollection from 'src/components/empty-collection/EmptyCollection';
import Loading from 'src/components/loading/Loading';

import {
  buildCatalogTree,
  type CatalogTreeRow,
  countSelection,
  expandableRowIds,
  filterCatalogTree,
} from './catalog-selection';

interface Props {
  nodes: CatalogNode[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  staleIds: string[];
  onRemoveStaleId: (id: string) => void;
  loading: boolean;
  error: boolean;
}

const collectDescendantIds = (
  rows: CatalogTreeRow[],
  into: Map<string, string[]>
): void => {
  for (const row of rows) {
    const ids: string[] = [];
    const walk = (children: CatalogTreeRow[]) => {
      for (const child of children) {
        ids.push(child.externalId);
        walk(child.children);
      }
    };
    walk(row.children);
    into.set(row.externalId, ids);
    collectDescendantIds(row.children, into);
  }
};

const indexRowsById = (
  rows: CatalogTreeRow[],
  into: Map<string, CatalogTreeRow>
): void => {
  for (const row of rows) {
    into.set(row.externalId, row);
    indexRowsById(row.children, into);
  }
};

export const CatalogPicker: FC<Props> = ({
  nodes,
  selectedIds,
  onChange,
  staleIds,
  onRemoveStaleId,
  loading,
  error,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'regulatoryFeed' });
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  // Filtering the full catalog (thousands of chapters) on every keystroke is
  // laggy. Keep the input bound to `query` for responsive typing, but debounce
  // the value that actually drives filtering so it runs once the user pauses.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), 250);

    return () => clearTimeout(handle);
  }, [query]);

  const fullTree = useMemo(() => buildCatalogTree(nodes), [nodes]);

  const { rowsById, descendantsById } = useMemo(() => {
    const rowsByIdMap = new Map<string, CatalogTreeRow>();
    const descendantsByIdMap = new Map<string, string[]>();
    indexRowsById(fullTree, rowsByIdMap);
    collectDescendantIds(fullTree, descendantsByIdMap);

    return { rowsById: rowsByIdMap, descendantsById: descendantsByIdMap };
  }, [fullTree]);

  const filteredTree = useMemo(
    () => filterCatalogTree(fullTree, debouncedQuery),
    [fullTree, debouncedQuery]
  );

  const isSearching = debouncedQuery.trim().length > 0;
  const expandedItems = useMemo(
    () =>
      (isSearching ? expandableRowIds(filteredTree) : expandedIds).map(
        (externalId) => ({
          externalId,
        })
      ),
    [isSearching, filteredTree, expandedIds]
  );

  const selectedItems = useMemo(
    () =>
      [...selectedIds]
        .map((id) => rowsById.get(id))
        .filter((row): row is CatalogTreeRow => Boolean(row)),
    [selectedIds, rowsById]
  );

  const counts = useMemo(
    () => countSelection(selectedIds, nodes),
    [selectedIds, nodes]
  );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Alert type={'error'}>{t('catalog_error')}</Alert>;
  }

  if (nodes.length === 0) {
    return (
      <EmptyCollection
        title={t('empty_title')}
        subtitle={t('empty_subtitle')}
        action={<></>}
      />
    );
  }

  return (
    <SpaceBetween size={'s'} direction={'vertical'}>
      <Box variant={'p'} color={'text-body-secondary'}>
        {t('picker_help')}
      </Box>

      {staleIds.length > 0 && (
        <Alert type={'warning'} header={t('stale_header')}>
          <SpaceBetween size={'xs'} direction={'vertical'}>
            {staleIds.map((id) => (
              <Box key={id}>
                {t('stale_row', { id })}{' '}
                <Button
                  variant={'inline-link'}
                  onClick={() => onRemoveStaleId(id)}
                >
                  {t('stale_remove')}
                </Button>
              </Box>
            ))}
          </SpaceBetween>
        </Alert>
      )}

      <Input
        type={'search'}
        value={query}
        onChange={({ detail }) => setQuery(detail.value)}
        placeholder={t('search_placeholder')}
        clearAriaLabel={t('search_clear')}
        ariaLabel={t('search_placeholder')}
      />

      <Box variant={'small'} color={'text-body-secondary'}>
        {counts.standards === 0 && counts.chapters === 0
          ? t('summary_none')
          : t('summary', {
              standards: counts.standards,
              chapters: counts.chapters,
            })}
      </Box>

      <Table
        variant={'embedded'}
        trackBy={'externalId'}
        items={filteredTree}
        selectionType={'multi'}
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => {
          const next = new Set<string>();
          for (const row of detail.selectedItems) {
            next.add(row.externalId);
            for (const id of descendantsById.get(row.externalId) ?? []) {
              next.add(id);
            }
          }
          onChange(next);
        }}
        expandableRows={{
          getItemChildren: (item) => item.children,
          isItemExpandable: (item) => item.children.length > 0,
          expandedItems,
          onExpandableItemToggle: ({ detail }) =>
            setExpandedIds((prev) => {
              const next = new Set(prev);
              if (detail.expanded) {
                next.add(detail.item.externalId);
              } else {
                next.delete(detail.item.externalId);
              }

              return [...next];
            }),
        }}
        columnDefinitions={[
          {
            id: 'title',
            header: t('column_name'),
            cell: (row) => row.title,
            isRowHeader: true,
          },
        ]}
        empty={<Box textAlign={'center'}>{t('no_matches')}</Box>}
      />
    </SpaceBetween>
  );
};

export default CatalogPicker;
